import { useState, useEffect, useCallback } from 'react';

export interface Task {
  id: string;
  title: string;
  quadrant: string;
  status: string;
  priority: string;
  area: string;
  assignee: string;
  dueDate: string | null;
}

export interface CostsData {
  mtd: number;
  projected: number;
  error: string | null;
}

export interface AgentStatus {
  status: string;
  runningCount: number;
  desiredCount: number;
  lastEvent?: string;
}

export interface AgentsData {
  moltbot: AgentStatus;
  architect: AgentStatus;
  error: string | null;
}

export interface ActivityEvent {
  timestamp: string;
  message: string;
  logStream: string;
}

export interface DashboardData {
  tasks: { tasks: Task[]; error: string | null };
  costs: CostsData;
  agents: AgentsData;
  activity: { events: ActivityEvent[]; error: string | null };
  timestamp: string;
}

export function useDashboardData(pollingInterval = 30000) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch('/api/dashboard');
      if (!response.ok) {
        if (response.status === 401) {
          window.location.href = '/login';
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }
      const result = await response.json();
      setData(result);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Polling
  useEffect(() => {
    if (pollingInterval <= 0) return;

    const interval = setInterval(fetchData, pollingInterval);
    return () => clearInterval(interval);
  }, [fetchData, pollingInterval]);

  return { data, loading, error, lastUpdated, refetch: fetchData };
}

export function useTasks(pollingInterval = 30000) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await fetch('/api/tasks');
      if (!response.ok) {
        if (response.status === 401) {
          window.location.href = '/login';
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }
      const result = await response.json();
      setTasks(result.tasks || []);
      setError(result.error);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    if (pollingInterval > 0) {
      const interval = setInterval(fetchTasks, pollingInterval);
      return () => clearInterval(interval);
    }
  }, [fetchTasks, pollingInterval]);

  return { tasks, loading, error, refetch: fetchTasks };
}

export function useCosts(pollingInterval = 60000) {
  const [costs, setCosts] = useState<CostsData>({ mtd: 0, projected: 0, error: null });
  const [loading, setLoading] = useState(true);

  const fetchCosts = useCallback(async () => {
    try {
      const response = await fetch('/api/costs');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      setCosts(result);
    } catch (err) {
      setCosts({ mtd: 0, projected: 0, error: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCosts();
    if (pollingInterval > 0) {
      const interval = setInterval(fetchCosts, pollingInterval);
      return () => clearInterval(interval);
    }
  }, [fetchCosts, pollingInterval]);

  return { costs, loading, refetch: fetchCosts };
}

export function useAgents(pollingInterval = 15000) {
  const [agents, setAgents] = useState<AgentsData>({
    moltbot: { status: 'unknown', runningCount: 0, desiredCount: 0 },
    architect: { status: 'unknown', runningCount: 0, desiredCount: 0 },
    error: null,
  });
  const [loading, setLoading] = useState(true);

  const fetchAgents = useCallback(async () => {
    try {
      const response = await fetch('/api/agents');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      setAgents(result);
    } catch (err) {
      setAgents({
        moltbot: { status: 'error', runningCount: 0, desiredCount: 0 },
        architect: { status: 'error', runningCount: 0, desiredCount: 0 },
        error: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
    if (pollingInterval > 0) {
      const interval = setInterval(fetchAgents, pollingInterval);
      return () => clearInterval(interval);
    }
  }, [fetchAgents, pollingInterval]);

  return { agents, loading, refetch: fetchAgents };
}
